/**
 * PendingInvitationsList Component
 *
 * Displays a list of pending invitations with:
 * - Invitation details (email, role, expiry)
 * - Resend functionality
 * - Revoke functionality
 *
 * Migrated onto the List & Grid Standard as an EMBEDDED list (`ListFrame`, not
 * `ListPageLayout`) — this is a child component rendered inside a tab of
 * `UsersRolesPage.tsx`, not a dedicated list page. See `src/pages/ApiKeysPage.tsx` for the
 * reference migration and `.specify/journals/list-grid-adoption-journal.md` for the recipe.
 */
import { useEffect, useMemo, useState } from 'react';
import type { FC, ReactNode } from 'react';
import {
  Card,
  CardHeader,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Badge,
  Button,
  Text,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Tooltip,
  tokens,
  makeStyles,
} from '@fluentui/react-components';
import {
  MailRegular,
  ArrowSyncRegular,
  DeleteRegular,
  ClockRegular,
  ArrowSort16Regular,
  ArrowSortUp16Regular,
  ArrowSortDown16Regular,
} from '@fluentui/react-icons';
import { useInvitations, useRevokeInvitation, useResendInvitation } from '../hooks';
import { useAuth } from '../../authentication';
import {
  ListFrame,
  ListToolbar,
  Pager,
  actionsColumnWidth,
  useListUrlState,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  ListLoadingOverlay,
} from '../../../components/list';
import type { SortDirection } from '../../../components/list';
import type { Invitation, TenantRole, InvitationStatus } from '../types';

/**
 * Roles allowed to manage invitations (resend / revoke). `InvitationsController` (API) has
 * no per-endpoint `RequireAnyRole` — it's class-level `[Authorize]` only — so there is no
 * controller role list to mirror directly. Instead this mirrors the page-level admin gate
 * `UsersRolesPage.tsx` already uses (`isAdmin`), which is the same gate that shows the
 * "Invite User" button and grants `TenantMembersList` its `canManageRoles`/`canRemoveMembers`
 * affordances — keeping invitation management and member management behind the same roles.
 */
const INVITATION_MANAGER_ROLES = ['System.Admin', 'Organization.Admin'];

const useStyles = makeStyles({
  card: {
    width: '100%',
  },
  cardBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingHorizontalM,
  },
  actionsCell: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
  },
  expiryWarning: {
    color: tokens.colorPaletteYellowForeground1,
  },
  expiryExpired: {
    color: tokens.colorPaletteRedForeground1,
  },
});

/**
 * Get badge color based on role
 */
function getRoleColor(role: TenantRole): 'brand' | 'important' | 'informative' | 'subtle' {
  switch (role) {
    case 'Owner':
      return 'important';
    case 'Admin':
      return 'brand';
    case 'SecurityEngineer':
    case 'Developer':
      return 'informative';
    default:
      return 'subtle';
  }
}

/**
 * Get status badge appearance
 */
function getStatusColor(
  status: InvitationStatus
): 'warning' | 'success' | 'danger' | 'subtle' {
  switch (status) {
    case 'Pending':
      return 'warning';
    case 'Accepted':
      return 'success';
    case 'Expired':
    case 'Revoked':
      return 'danger';
    default:
      return 'subtle';
  }
}

/**
 * Check if invitation is expiring soon (within 24 hours)
 */
function isExpiringSoon(expiresAt: string): boolean {
  const expiry = new Date(expiresAt);
  const now = new Date();
  const hoursUntilExpiry = (expiry.getTime() - now.getTime()) / (1000 * 60 * 60);
  return hoursUntilExpiry > 0 && hoursUntilExpiry <= 24;
}

/**
 * Check if invitation is expired
 */
function isExpired(expiresAt: string): boolean {
  return new Date(expiresAt) < new Date();
}

/**
 * Format relative time
 */
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  if (diffMs < 0) {
    return 'Expired';
  }
  if (diffHours < 1) {
    return 'Expires soon';
  }
  if (diffHours < 24) {
    return `${diffHours} hours`;
  }
  return `${diffDays} days`;
}

/** Sort-field accessor for the client-side comparator below. */
function getSortValue(invitation: Invitation, field: string): string {
  switch (field) {
    case 'role':
      return invitation.role;
    case 'status':
      return invitation.status;
    case 'expiresAt':
      return invitation.expiresAt;
    case 'invitedByName':
      return invitation.invitedByName ?? '';
    case 'email':
    default:
      return invitation.email;
  }
}

export interface PendingInvitationsListProps {
  tenantId: string;
  showAllStatuses?: boolean;
}

export const PendingInvitationsList: FC<PendingInvitationsListProps> = ({
  tenantId,
  showAllStatuses = false,
}) => {
  const styles = useStyles();
  const { user } = useAuth();
  const canManageInvitations =
    user?.roles?.some((role) => INVITATION_MANAGER_ROLES.includes(role)) ?? false;

  const {
    data: allInvitations,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useInvitations(tenantId);
  const revokeInvitation = useRevokeInvitation();
  const resendInvitation = useResendInvitation();

  const [invitationToRevoke, setInvitationToRevoke] = useState<Invitation | null>(null);

  const { state, setPage, setSort, setSearch } = useListUrlState({ defaultSort: 'email:asc' });
  const [sortField, sortDirRaw] = state.sort!.split(':');
  const sortDir: SortDirection = sortDirRaw === 'desc' ? 'desc' : 'asc';

  const toggleSort = (field: string) => {
    if (field === sortField) {
      setSort(`${field}:${sortDir === 'asc' ? 'desc' : 'asc'}`);
    } else {
      setSort(`${field}:asc`);
    }
  };

  const sortIcon = (field: string) => {
    if (field !== sortField) return <ArrowSort16Regular />;
    return sortDir === 'asc' ? <ArrowSortUp16Regular /> : <ArrowSortDown16Regular />;
  };

  // Client-side filter -> search -> sort -> page-slice over the tenant's invitations, which
  // `useInvitations` already fetches unpaged in full. This mirrors `useAdaptiveGridData`'s
  // client-mode semantics (§ MIGRATION RECIPE: "useAdaptiveGridData or slice") without
  // wrapping already-resolved data in a redundant second query.
  const filteredSorted = useMemo(() => {
    const base = showAllStatuses
      ? (allInvitations ?? [])
      : (allInvitations ?? []).filter((inv) => inv.status === 'Pending');

    const needle = state.search.trim().toLowerCase();
    const searched = needle
      ? base.filter(
          (inv) =>
            inv.email.toLowerCase().includes(needle) ||
            inv.role.toLowerCase().includes(needle) ||
            inv.status.toLowerCase().includes(needle) ||
            (inv.invitedByName ?? '').toLowerCase().includes(needle)
        )
      : base;

    return [...searched].sort((a, b) => {
      const cmp = getSortValue(a, sortField).toLowerCase().localeCompare(getSortValue(b, sortField).toLowerCase());
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [allInvitations, showAllStatuses, state.search, sortField, sortDir]);

  const totalCount = filteredSorted.length;
  const totalPages = Math.max(Math.ceil(totalCount / state.pageSize), 1);
  const pageStart = (state.page - 1) * state.pageSize;
  const invitations = filteredSorted.slice(pageStart, pageStart + state.pageSize);
  const hasPreviousPage = state.page > 1;
  const hasNextPage = state.page < totalPages;

  // Out-of-range clamp (§9) — never strand the user on an empty page after search/revoke.
  useEffect(() => {
    if (!isLoading && state.page > totalPages) {
      setPage(totalPages);
    }
  }, [isLoading, state.page, totalPages, setPage]);

  const handleResend = async (invitation: Invitation) => {
    await resendInvitation.mutateAsync({
      tenantId: invitation.tenantId,
      invitationId: invitation.id,
    });
  };

  const handleRevoke = async () => {
    if (invitationToRevoke) {
      await revokeInvitation.mutateAsync({
        tenantId: invitationToRevoke.tenantId,
        invitationId: invitationToRevoke.id,
      });
      setInvitationToRevoke(null);
    }
  };

  const actionButtonCount = 2; // Resend + Revoke
  const columnCount = canManageInvitations ? 6 : 5;

  let listBody: ReactNode;
  if (isLoading) {
    listBody = <LoadingSkeleton rows={5} columns={columnCount} />;
  } else if (isError) {
    listBody = (
      <ErrorState
        message={error instanceof Error ? error.message : undefined}
        onRetry={refetch}
      />
    );
  } else if (invitations.length === 0) {
    listBody = (
      <EmptyState
        icon={<MailRegular />}
        title={showAllStatuses ? 'No Invitations' : 'No Pending Invitations'}
        description={
          totalCount === 0 && !state.search
            ? 'No pending invitations. Invite users to get started.'
            : 'No invitations match your search.'
        }
      />
    );
  } else {
    listBody = (
      <ListLoadingOverlay busy={isFetching && !isLoading}>
        <ListFrame>
          <Table aria-label="Pending invitations">
            <TableHeader>
              <TableRow>
                <TableHeaderCell
                  sortable
                  sortDirection={sortField === 'email' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                  sortIcon={sortIcon('email')}
                  onClick={() => toggleSort('email')}
                  style={{ width: '100%' }}
                >
                  Email
                </TableHeaderCell>
                <TableHeaderCell
                  sortable
                  sortDirection={sortField === 'role' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                  sortIcon={sortIcon('role')}
                  onClick={() => toggleSort('role')}
                >
                  Role
                </TableHeaderCell>
                <TableHeaderCell
                  sortable
                  sortDirection={sortField === 'status' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                  sortIcon={sortIcon('status')}
                  onClick={() => toggleSort('status')}
                >
                  Status
                </TableHeaderCell>
                <TableHeaderCell
                  sortable
                  sortDirection={
                    sortField === 'expiresAt' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined
                  }
                  sortIcon={sortIcon('expiresAt')}
                  onClick={() => toggleSort('expiresAt')}
                >
                  Expires
                </TableHeaderCell>
                <TableHeaderCell
                  sortable
                  sortDirection={
                    sortField === 'invitedByName' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined
                  }
                  sortIcon={sortIcon('invitedByName')}
                  onClick={() => toggleSort('invitedByName')}
                >
                  Invited By
                </TableHeaderCell>
                {canManageInvitations && (
                  <TableHeaderCell style={{ width: actionsColumnWidth(actionButtonCount) }}>
                    Actions
                  </TableHeaderCell>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {invitations.map((invitation) => {
                const expired = isExpired(invitation.expiresAt);
                const expiringSoon = isExpiringSoon(invitation.expiresAt);
                const isPending = invitation.status === 'Pending';
                const isResending =
                  resendInvitation.isPending && resendInvitation.variables?.invitationId === invitation.id;
                const isRevoking =
                  revokeInvitation.isPending && revokeInvitation.variables?.invitationId === invitation.id;

                return (
                  <TableRow key={invitation.id}>
                    <TableCell>
                      <Text weight="semibold">{invitation.email}</Text>
                    </TableCell>
                    <TableCell>
                      <Badge
                        appearance="filled"
                        color={getRoleColor(invitation.role as TenantRole)}
                      >
                        {invitation.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge
                        appearance="outline"
                        color={getStatusColor(invitation.status as InvitationStatus)}
                      >
                        {invitation.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span
                        className={
                          expired
                            ? styles.expiryExpired
                            : expiringSoon
                            ? styles.expiryWarning
                            : undefined
                        }
                      >
                        <ClockRegular style={{ marginRight: '4px' }} />
                        {formatRelativeTime(invitation.expiresAt)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Text size={200}>{invitation.invitedByName || 'Unknown'}</Text>
                    </TableCell>
                    {canManageInvitations && (
                      <TableCell>
                        {/* Hidden-not-disabled (§18): only Pending invitations expose
                            Resend/Revoke; Accepted/Expired/Revoked rows render no actions.
                            The whole column is omitted above for non-managers. */}
                        {isPending && (
                          <div className={styles.actionsCell}>
                            <Tooltip content="Resend invitation" relationship="label">
                              <Button
                                icon={<ArrowSyncRegular />}
                                appearance="subtle"
                                size="small"
                                aria-label="Resend invitation"
                                onClick={() => handleResend(invitation)}
                                disabled={isResending}
                              />
                            </Tooltip>
                            <Tooltip content="Revoke invitation" relationship="label">
                              <Button
                                icon={<DeleteRegular />}
                                appearance="subtle"
                                size="small"
                                aria-label="Revoke invitation"
                                onClick={() => setInvitationToRevoke(invitation)}
                                disabled={isRevoking}
                              />
                            </Tooltip>
                          </div>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </ListFrame>
      </ListLoadingOverlay>
    );
  }

  return (
    <>
      <Card className={styles.card}>
        <CardHeader
          image={<MailRegular />}
          header={
            <Text weight="semibold">
              {showAllStatuses ? 'Invitations' : 'Pending Invitations'} ({totalCount})
            </Text>
          }
        />
        <div className={styles.cardBody}>
          <ListToolbar
            search={state.search}
            onSearchChange={setSearch}
            searchPlaceholder="Search invitations…"
            onRefresh={refetch}
            isRefreshing={isFetching}
            testIdPrefix="pending-invitations"
          />
          {listBody}
          {!isLoading && !isError && invitations.length > 0 && (
            <Pager
              page={state.page}
              totalPages={totalPages}
              hasPreviousPage={hasPreviousPage}
              hasNextPage={hasNextPage}
              totalCount={totalCount}
              onPageChange={setPage}
              testId="pending-invitations-pager"
            />
          )}
        </div>
      </Card>

      {/* Revoke Invitation Confirmation Dialog */}
      <Dialog
        open={!!invitationToRevoke}
        onOpenChange={(_, data) => !data.open && setInvitationToRevoke(null)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Revoke Invitation</DialogTitle>
            <DialogContent>
              <Text>
                Are you sure you want to revoke the invitation for{' '}
                <Text weight="semibold">{invitationToRevoke?.email}</Text>? They will
                no longer be able to join using this invitation link.
              </Text>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setInvitationToRevoke(null)}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleRevoke}
                disabled={revokeInvitation.isPending}
              >
                {revokeInvitation.isPending ? 'Revoking...' : 'Revoke Invitation'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
};
